import { AxiosError } from "axios";
import { AlertCircle } from "lucide-react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import ModernPageLayout from "@/components/layout/ModernPageLayout";
import { Alert, AlertDescription } from "@/components/ui/v2/alert";
import { Skeleton } from "@/components/ui/v2/skeleton";
import { ROUTES } from "@/constants/routes";
import { useUpdateUserMutation } from "@/features/users/api/useUpdateUserMutation";
import { useUser } from "@/features/users/api/useUser";
import { EditUserFormPayload } from "@/features/users/components/UserForm";
import EditUserForm from "@/features/users/components/UserForm/EditUserForm";
import { handleProblemDetailsError } from "@/lib/helpers/handleProblemDetailsError";
import { useAuth } from "@/lib/hooks/useAuth";
import { useBreadcrumbs } from "@/lib/hooks/useBreadcrumbs";
import useToast from "@/lib/hooks/useToast";
import useUserRoleOptions from "@/lib/hooks/useUserRoleOptions";
import { ValidationResponse } from "@/types/response/ValidationResponse";

type EditUserPageProps = {
  userId: string;
  clientId?: string;
};

function EditUserPageContent({ userId, clientId }: EditUserPageProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { tenantId, context, userId: currentUserId, role: currentRole } = useAuth();
  const isEditingSelf = userId === currentUserId;
  const roleContext = clientId ? "client" : context ?? "admin";
  const { getRoleOptions } = useUserRoleOptions(roleContext, isEditingSelf ? currentRole ?? undefined : undefined);

  const {
    data: user,
    isLoading: userLoading,
    isFetching: userFetching,
    error: userError,
  } = useUser(userId);

  const basePath = clientId
    ? `/${ROUTES.clients}/${clientId}/${ROUTES.users}`
    : `/${ROUTES.users}`;

  const handleNavigateBack = useCallback(() => {
    navigate(basePath);
  }, [navigate, basePath]);

  const resolvedTenantId = !clientId && context === "admin" ? tenantId : null;

  const updateUser = useUpdateUserMutation({
    userId,
    tenantId: resolvedTenantId,
    clientId: clientId || null,
  });

  const handleSubmit = useCallback(
    (payload: EditUserFormPayload) => {
      updateUser.mutate(
        {
          ...payload,
          clientId: clientId ?? "",
          tenantId: tenantId ?? "",
        },
        {
          onSuccess: () => {
            handleNavigateBack();
          },
          onError: (
            error: AxiosError<
              | ValidationResponse
              | {
                  title?: string;
                  detail?: string;
                  status?: number;
                  field?: string;
                }
            >,
          ) => {
            const response = error.response?.data;

            if (Array.isArray(response)) {
              const message = response
                .map(({ errorMessage }) => t(`validation.${errorMessage}`))
                .join(", ");
              showToast(message, "error");
              return;
            }

            handleProblemDetailsError({
              error: error as AxiosError<{
                title: string;
                detail: string;
                status?: number;
                field?: string;
              }>,
              t,
            });
          },
        },
      );
    },
    [updateUser, handleNavigateBack, t, showToast, clientId, tenantId],
  );

  const titleKey =
    context === "admin"
      ? "admin.userManagement.editUser.title"
      : "client.userManagement.editUser.title";
  const subtitleKey =
    context === "admin"
      ? "admin.userManagement.editUser.subtitle"
      : "client.userManagement.editUser.subtitle";

  const breadcrumbs = useBreadcrumbs({
    items: [
      { label: t("common.users"), href: basePath },
      {
        label:
          user?.firstName && user?.lastName
            ? `${user.firstName} ${user.lastName}`
            : user?.email || t("common.user"),
        href: `${basePath}/${userId}`,
      },
      { label: t("common.edit") },
    ],
  });

  if (userLoading || userFetching) {
    return (
      <ModernPageLayout
        breadcrumbs={breadcrumbs}
        backbutton={{
          href: basePath,
          label: t("common.back"),
        }}
        title={t(titleKey)}
        subtitle={t(subtitleKey)}
      >
        <div className="space-y-4">
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      </ModernPageLayout>
    );
  }

  if (userError || !user) {
    return (
      <ModernPageLayout
        breadcrumbs={breadcrumbs}
        backbutton={{
          href: basePath,
          label: t("common.back"),
        }}
        title={t(titleKey)}
        subtitle={t(subtitleKey)}
      >
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {t("errors.invalidEntity", { entity: t("common.user") })}
          </AlertDescription>
        </Alert>
      </ModernPageLayout>
    );
  }

  return (
    <ModernPageLayout
      breadcrumbs={breadcrumbs}
      backbutton={{
        href: basePath,
        label: t("common.back"),
      }}
      title={t(titleKey)}
      subtitle={t(subtitleKey)}
    >
      <EditUserForm
        user={user}
        options={getRoleOptions()}
        isEditingSelf={isEditingSelf}
        isSubmitting={updateUser.isPending}
        onSubmitForm={handleSubmit}
        onSuccess={handleNavigateBack}
      />
    </ModernPageLayout>
  );
}

function EditUserPage() {
  const { userId, clientId } = useParams();
  if (!userId) {
    return null;
  }

  return <EditUserPageContent userId={userId} clientId={clientId} />;
}

export default EditUserPage;
